import axios, { AxiosError } from "axios";
import axiosRetry from "axios-retry";
import { Dictionary } from "lodash";
import keycloak, { forceLogin, updateToken } from "./keycloak";
import { isAuthErrored } from "./utils/isAuthError";

const setAuthorization = (token: string) =>
  (axios.defaults.headers.common["Authorization"] = `Bearer ${token}`);

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-User-Timezone"] = getBrowserTimezone();
  return config;
});

// axios.defaults.baseURL = config.ruleServerUrl; // TODO: Need to switch everything api Server
const init = ({ token }: Dictionary<string>) => {
  axiosRetry(axios, {
    retries: 2,
    retryCondition: async (error: AxiosError) => {
      if (isAuthErrored(error as AxiosError<Dictionary<string>>)) {
        keycloak.onTokenExpired && keycloak.onTokenExpired();
        return true;
      }
      return false;
    },
  });
  setAuthorization(token);
};

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];
function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(newToken: string) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}
const isUnauthorized = (error: AxiosError) => {
  return error.response?.status === 401 || error.response?.status === 403;
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isUnauthorized(error)) {
      const originalRequest = error.config;
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (!isRefreshing) {
          isRefreshing = true;
          try {
            const newToken = await updateToken();
            if (newToken) {
              onRefreshed(newToken);
              isRefreshing = false;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (err) {
            isRefreshing = false;
            return Promise.reject(err);
          }
        }

        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axios(originalRequest));
          });
        });
      } else {
        await forceLogin();
      }
    }
    throw error;
  }
);

const Axios = {
  init,
  setAuthorization,
};

export default Axios;
